import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, CheckCircle2, Quote, Rocket, ShieldCheck, Award, MonitorPlay, Lightbulb, GraduationCap } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CourseCard } from "@/components/site/CourseCard";
import { Reveal, SectionTitle, Empty, Spinner } from "@/components/site/Bits";
import { useSite, usePageTitle } from "@/lib/site";
import { publicApi, fileUrl, fmtDate } from "@/lib/api";

const ICONS = [Rocket, GraduationCap, Lightbulb, Award, MonitorPlay, ShieldCheck];

const Hero = ({ s, next }) => (
  <section className="relative overflow-hidden grain" data-testid="home-hero">
    <div className="absolute inset-0 pattern-arch opacity-60" />
    <div className="container-x relative z-10 grid lg:grid-cols-12 gap-12 items-center py-16 md:py-24">
      <div className="lg:col-span-7">
        <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-white/70 px-4 py-1.5 text-sm text-[var(--copper)] font-semibold">
          <Sparkles size={16} /> {s.slogan}
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.7 }} className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.25] text-[var(--ink)]" data-testid="hero-title">
          {s.hero_title?.includes("…") ? (
            <>{s.hero_title.split("…")[0]}…<br /><span className="gold-text">{s.hero_title.split("…").slice(1).join("…")}</span></>
          ) : s.hero_title}
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mt-6 text-base md:text-lg leading-loose text-[var(--muted-ink)] max-w-2xl" data-testid="hero-subtitle">{s.hero_subtitle}</motion.p>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="mt-9 flex flex-wrap gap-4">
          <Link to="/courses" className="btn-copper" data-testid="hero-explore-btn">استكشف الدورات <ArrowLeft size={18} /></Link>
          <Link to={next ? `/enroll/${next.id}` : "/courses"} className="btn-gold-outline" data-testid="hero-book-btn">احجز مقعدك الآن</Link>
        </motion.div>
      </div>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, duration: 0.8 }} className="lg:col-span-5 relative">
        <div className="relative rounded-[2rem] overflow-hidden border-4 border-white shadow-2xl aspect-[4/5]">
          <img src={fileUrl(s.hero_image) || "/images/hero.jpg"} alt="تدريب أكاديمية هِمّة تم" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1F1815]/70 via-transparent" />
          {next && (
            <div className="absolute bottom-5 inset-x-5 rounded-2xl bg-white/85 backdrop-blur-xl p-4 border border-white" data-testid="hero-next-course">
              <p className="text-xs text-[var(--copper)] font-semibold">الدورة القادمة</p>
              <p className="font-display font-bold text-sm mt-1 line-clamp-2">{next.title}</p>
              <p className="text-xs text-[var(--muted-ink)] mt-1">{fmtDate(next.date)} — متبقٍ {next.seats_left} مقعد</p>
            </div>
          )}
        </div>
        <div className="floaty absolute -top-6 -left-4 hidden md:flex items-center gap-2 rounded-2xl bg-white shadow-xl px-4 py-3 border border-[var(--line)]">
          <CheckCircle2 className="text-[var(--gold)]" /> <span className="text-sm font-semibold">بدون خبرة سابقة</span>
        </div>
      </motion.div>
    </div>
  </section>
);

const Features = ({ items }) => (
  <section className="container-x py-16 md:py-24" data-testid="home-features">
    <SectionTitle eyebrow="لماذا هِمّة تم؟" title="مميزات الأكاديمية" />
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((f, i) => {
        const Icon = ICONS[i % ICONS.length];
        return (
          <Reveal key={i} delay={i * 0.06}>
            <div className="card-lux p-7 h-full">
              <div className="w-12 h-12 rounded-xl grid place-items-center bg-[#B87333]/10 text-[var(--copper)] mb-5"><Icon size={22} /></div>
              <h3 className="font-display font-bold text-lg mb-2">{f.title}</h3>
              <p className="text-sm leading-relaxed text-[var(--muted-ink)]">{f.text}</p>
            </div>
          </Reveal>
        );
      })}
    </div>
  </section>
);

const Testimonials = ({ items }) => (
  <section className="bg-[#1F1815] py-16 md:py-24 relative overflow-hidden" data-testid="home-testimonials">
    <div className="container-x">
      <p className="eyebrow mb-2 !text-[var(--gold)]">قصص المتدربين</p>
      <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-10">آراء المتدربين</h2>
      <div className="grid gap-6 md:grid-cols-3">
        {items.map((t, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-7 h-full">
              <Quote className="text-[var(--gold)] mb-4" />
              <p className="text-white/85 leading-loose text-sm">{t.text}</p>
              <p className="mt-5 font-bold text-white">{t.name}</p>
              <p className="text-xs text-white/50">{t.role}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const FaqList = ({ items }) => (
  <Accordion type="single" collapsible className="space-y-3" data-testid="faq-list">
    {items.map((f, i) => (
      <AccordionItem key={i} value={`f${i}`} className="card-lux px-6 !border">
        <AccordionTrigger className="text-right font-display font-semibold hover:no-underline" data-testid={`faq-item-${i}`}>{f.q}</AccordionTrigger>
        <AccordionContent className="text-[var(--muted-ink)] leading-loose">{f.a}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

const Grid = ({ list, empty }) =>
  !list ? <Spinner /> : list.length ? (
    <div className="grid gap-7 md:grid-cols-2 lg:grid-cols-3">{list.map((c, i) => <CourseCard key={c.id} course={c} index={i} />)}</div>
  ) : <Empty text={empty} />;

export default function Home() {
  usePageTitle("");
  const { settings: s } = useSite();
  const [featured, setFeatured] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  useEffect(() => {
    publicApi.get("/courses", { params: { featured: true } }).then((r) => setFeatured(r.data.courses));
    publicApi.get("/courses", { params: { upcoming: true } }).then((r) => setUpcoming(r.data.courses));
  }, []);
  if (!s) return <Spinner />;
  const next = upcoming?.find((c) => c.can_register);
  return (
    <div data-testid="home-page">
      <Hero s={s} next={next} />
      <section className="container-x py-16 md:py-24" data-testid="home-featured">
        <SectionTitle eyebrow="الأكثر طلباً" title="الدورات المميزة" action={<Link to="/courses" className="text-[var(--copper)] font-semibold inline-flex items-center gap-1" data-testid="home-all-courses-link">جميع الدورات <ArrowLeft size={16} /></Link>} />
        <Grid list={featured} empty="لا توجد دورات مميزة حالياً" />
      </section>
      <section className="bg-[var(--paper)] py-16 md:py-24 border-y border-[var(--line)]" data-testid="home-upcoming">
        <div className="container-x">
          <SectionTitle eyebrow="احجز مبكراً" title="الدورات القادمة" />
          <Grid list={upcoming} empty="لا توجد دورات قادمة حالياً، تابعنا قريباً" />
        </div>
      </section>
      <Features items={s.features || []} />
      <Testimonials items={s.testimonials || []} />
      <section className="container-x py-16 md:py-24 grid lg:grid-cols-12 gap-10" data-testid="home-faq">
        <div className="lg:col-span-4">
          <p className="eyebrow mb-2">لديك سؤال؟</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold">الأسئلة الشائعة</h2>
          <p className="mt-4 text-[var(--muted-ink)]">لم تجد إجابتك؟ <Link to="/contact" className="text-[var(--copper)] font-semibold">تواصل معنا</Link></p>
        </div>
        <div className="lg:col-span-8"><FaqList items={s.faqs || []} /></div>
      </section>
      <section className="container-x">
        <Reveal>
          <div className="rounded-[2rem] p-10 md:p-14 text-center text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, var(--copper), #6b3d17)" }} data-testid="home-cta">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold">ابدأ رحلتك مع هِمّة تم اليوم</h2>
            <p className="mt-3 text-white/85">{s.slogan}</p>
            <Link to="/courses" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-[var(--ink)] px-7 py-3 font-bold hover:bg-[#F7E8A4] transition-colors" data-testid="cta-explore-btn">استكشف الدورات <ArrowLeft size={18} /></Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
